import { ApiWorkoutDay, GenerateProgramResponse } from './types';

export interface ExerciseData {
  id: string;
  name: string;
  tags: { label: string; type: string }[];
  supersetId?: string;
  isRest?: boolean;
  restDuration?: string;
  sets?: number;
  reps?: number;
  duration?: number;
  restBetweenSets?: string;
  restAfterExercise?: string;
  metricType?: 'reps' | 'duration';
  image_male_url?: string;
  image_female_url?: string;
}

export type SectionData = {
  warmup: ExerciseData[];
  main: ExerciseData[];
  cooldown: ExerciseData[];
};

export type ProgramData = Record<number, Record<number, SectionData>>;

export const transformApiToProgramData = (
  apiResponse: GenerateProgramResponse,
  scheduleDaysPerWeek: number,
): ProgramData => {
  const rawData = apiResponse.program?.data || apiResponse.result?.program?.data;
  const apiDays: ApiWorkoutDay[] = rawData?.datetimeList || [];
  const programData: ProgramData = {};

  if (apiDays.length === 0) return programData;

  // Extract total program duration in weeks, default to 1 if missing
  const durationStr = rawData?.duration;

  // Sort apiDays chronologically to maintain logical workout order within a week
  const sortedDays = [...apiDays].sort(
    (a, b) => new Date(a.ymd).getTime() - new Date(b.ymd).getTime(),
  );

  if (sortedDays.length > 0) {
    const daysPerWeek =
      scheduleDaysPerWeek > 0
        ? scheduleDaysPerWeek
        : (parseInt(durationStr || '1', 10) > 0 &&
          sortedDays.length >= parseInt(durationStr || '1', 10)
            ? Math.ceil(sortedDays.length / parseInt(durationStr || '1', 10))
            : sortedDays.length) || 1;

    sortedDays.forEach((apiDay, index) => {
      const weekNum = Math.floor(index / daysPerWeek) + 1;

      if (!programData[weekNum]) {
        programData[weekNum] = {};
      }
      const currentWorkout = Object.keys(programData[weekNum]).length + 1;

      const rawWarmup =
        apiDay.warmUpMoves ||
        (apiDay as any).warm_up_moves ||
        (apiDay as any).warmupMoves ||
        (apiDay as any).warmup_moves ||
        [];
      const warmup: ExerciseData[] = rawWarmup.map((m: any) => {
        const isRest = m.title?.toLowerCase() === 'rest';
        const restMatch = m.rest
          ? String(m.rest).match(/\d+/)
          : m.restAfterExercise
            ? String(m.restAfterExercise).match(/\d+/)
            : m.restBetweenSets
              ? String(m.restBetweenSets).match(/\d+/)
              : null;
        const restNum = restMatch ? parseInt(restMatch[0], 10) : 0;
        const restStr = restNum > 0 ? `${restNum}s` : '0s';
        return {
          id: `ex-${Date.now()}-${Math.random()}`,
          name: m.title,
          tags: [{ label: 'Warmup', type: 'section' }],
          sets: 1,
          metricType: 'duration',
          duration: m.duration,
          restBetweenSets: restStr,
          restAfterExercise: restStr,
          isRest,
          restDuration: isRest ? `${m.duration || 30} sec` : undefined,
        };
      });
      const rawMain =
        apiDay.moves ||
        (apiDay as any).main ||
        (apiDay as any).mainMoves ||
        (apiDay as any).main_moves ||
        [];
      const main: ExerciseData[] = rawMain.map((m: any) => {
        const isRest = m.title?.toLowerCase() === 'rest';
        const setsCount = m.list?.length || 1;
        const firstSet = m.list?.[0];
        const restMatch = firstSet?.rest ? String(firstSet.rest).match(/\d+/) : null;
        const restNum = restMatch ? parseInt(restMatch[0], 10) : 0;
        const restStr = restNum > 0 ? `${restNum}s` : '0s';

        return {
          id: `ex-${Date.now()}-${Math.random()}`,
          name: m.title,
          tags: [{ label: 'Main', type: 'section' }],
          sets: setsCount,
          metricType: firstSet?.time ? 'duration' : 'reps',
          reps: firstSet?.reps ? parseInt(firstSet.reps, 10) : undefined,
          duration: firstSet?.time ? parseInt(firstSet.time, 10) : undefined,
          restBetweenSets: restStr,
          restAfterExercise: restStr,
          isRest,
          restDuration: isRest ? `${firstSet?.time || firstSet?.rest || 30} sec` : undefined,
        };
      });

      const rawCooldown =
        apiDay.coolDownMoves ||
        (apiDay as any).cool_down_moves ||
        (apiDay as any).cooldownMoves ||
        (apiDay as any).cooldown_moves ||
        [];
      const cooldown: ExerciseData[] = rawCooldown.map((m: any) => {
        const isRest = m.title?.toLowerCase() === 'rest';
        const restMatch = m.rest
          ? String(m.rest).match(/\d+/)
          : m.restAfterExercise
            ? String(m.restAfterExercise).match(/\d+/)
            : m.restBetweenSets
              ? String(m.restBetweenSets).match(/\d+/)
              : null;
        const restNum = restMatch ? parseInt(restMatch[0], 10) : 0;
        const restStr = restNum > 0 ? `${restNum}s` : '0s';
        return {
          id: `ex-${Date.now()}-${Math.random()}`,
          name: m.title,
          tags: [{ label: 'Cooldown', type: 'section' }],
          sets: 1,
          metricType: 'duration',
          duration: m.duration,
          restBetweenSets: restStr,
          restAfterExercise: restStr,
          isRest,
          restDuration: isRest ? `${m.duration || 30} sec` : undefined,
        };
      });

      programData[weekNum][currentWorkout] = {
        warmup,
        main,
        cooldown,
      };
    });
  }

  return programData;
};

export const transformFinalPlanToProgramData = (
  apiResponse: any, // FinalProgrammePlan
  scheduleDaysPerWeek: number,
): ProgramData => {
  const apiDays: ApiWorkoutDay[] = apiResponse?.datetimeList || [];
  const programData: ProgramData = {};

  if (apiDays.length === 0) return programData;

  const durationStr = apiResponse?.duration;

  const sortedDays = [...apiDays].sort(
    (a, b) => new Date(a.ymd).getTime() - new Date(b.ymd).getTime(),
  );

  if (sortedDays.length > 0) {
    const daysPerWeek =
      scheduleDaysPerWeek > 0
        ? scheduleDaysPerWeek
        : (parseInt(durationStr || '1', 10) > 0 &&
          sortedDays.length >= parseInt(durationStr || '1', 10)
            ? Math.ceil(sortedDays.length / parseInt(durationStr || '1', 10))
            : sortedDays.length) || 1;

    sortedDays.forEach((apiDay, index) => {
      const weekNum = Math.floor(index / daysPerWeek) + 1;

      if (!programData[weekNum]) {
        programData[weekNum] = {};
      }
      const currentWorkout = Object.keys(programData[weekNum]).length + 1;

      const rawWarmup =
        apiDay.warmUpMoves ||
        (apiDay as any).warm_up_moves ||
        (apiDay as any).warmupMoves ||
        (apiDay as any).warmup_moves ||
        [];
      const warmup: ExerciseData[] = rawWarmup.map((m: any) => {
        const isRest = m.title?.toLowerCase() === 'rest';
        const restMatch = m.rest
          ? String(m.rest).match(/\d+/)
          : m.restAfterExercise
            ? String(m.restAfterExercise).match(/\d+/)
            : m.restBetweenSets
              ? String(m.restBetweenSets).match(/\d+/)
              : null;
        const restNum = restMatch ? parseInt(restMatch[0], 10) : 0;
        const restStr = restNum > 0 ? `${restNum}s` : '0s';
        return {
          id: `ex-${Date.now()}-${Math.random()}`,
          name: m.title,
          tags: [{ label: 'Warmup', type: 'section' }],
          sets: 1,
          metricType: 'duration',
          duration: m.duration,
          restBetweenSets: restStr,
          restAfterExercise: restStr,
          isRest,
          restDuration: isRest ? `${m.duration || 30} sec` : undefined,
        };
      });
      const rawMain =
        apiDay.moves ||
        (apiDay as any).main ||
        (apiDay as any).mainMoves ||
        (apiDay as any).main_moves ||
        [];
      const main: ExerciseData[] = rawMain.map((m: any) => {
        const isRest = m.title?.toLowerCase() === 'rest';
        const setsCount = m.list?.length || 1;
        const firstSet = m.list?.[0];
        const restMatch = firstSet?.rest ? String(firstSet.rest).match(/\d+/) : null;
        const restNum = restMatch ? parseInt(restMatch[0], 10) : 0;
        const restStr = restNum > 0 ? `${restNum}s` : '0s';

        return {
          id: `ex-${Date.now()}-${Math.random()}`,
          name: m.title,
          tags: [{ label: 'Main', type: 'section' }],
          sets: setsCount,
          metricType: firstSet?.time ? 'duration' : 'reps',
          reps: firstSet?.reps ? parseInt(firstSet.reps, 10) : undefined,
          duration: firstSet?.time ? parseInt(firstSet.time, 10) : undefined,
          restBetweenSets: restStr,
          restAfterExercise: restStr,
          isRest,
          restDuration: isRest ? `${firstSet?.time || firstSet?.rest || 30} sec` : undefined,
        };
      });

      const rawCooldown =
        apiDay.coolDownMoves ||
        (apiDay as any).cool_down_moves ||
        (apiDay as any).cooldownMoves ||
        (apiDay as any).cooldown_moves ||
        [];
      const cooldown: ExerciseData[] = rawCooldown.map((m: any) => {
        const isRest = m.title?.toLowerCase() === 'rest';
        const restMatch = m.rest
          ? String(m.rest).match(/\d+/)
          : m.restAfterExercise
            ? String(m.restAfterExercise).match(/\d+/)
            : m.restBetweenSets
              ? String(m.restBetweenSets).match(/\d+/)
              : null;
        const restNum = restMatch ? parseInt(restMatch[0], 10) : 0;
        const restStr = restNum > 0 ? `${restNum}s` : '0s';
        return {
          id: `ex-${Date.now()}-${Math.random()}`,
          name: m.title,
          tags: [{ label: 'Cooldown', type: 'section' }],
          sets: 1,
          metricType: 'duration',
          duration: m.duration,
          restBetweenSets: restStr,
          restAfterExercise: restStr,
          isRest,
          restDuration: isRest ? `${m.duration || 30} sec` : undefined,
        };
      });

      programData[weekNum][currentWorkout] = {
        warmup,
        main,
        cooldown,
      };
    });
  }

  return programData;
};
